import React, {type ReactNode, createContext, useContext} from 'react';
import {gql, useQuery} from '@apollo/client';
import type {DeliveryManagementCurrentAccountStoresQuery} from '../../__generated__/DeliveryManagementCurrentAccountStoresQuery';
import Loader from '../Loader';
import {Layout, MainContent} from '../Layout';
import {signInUrlWithDestUrl} from '../../paths';

type Store = {
  uuid: string;
  name: string;
  address: {street: string} | null;
};

type CurrentAccountStoresType = {
  stores: Store[];
};

export const CurrentAccountStoresContext = createContext<CurrentAccountStoresType>({
  stores: [],
});

export const CurrentAccountStoresQuery = gql`
  query DeliveryManagementCurrentAccountStoresQuery {
    me {
      id
      catererAccount {
        id
        stores {
          nodes {
            id
            uuid
            name
            address {
              id
              street
            }
          }
        }
      }
    }
  }
`;

const performLoginRedirect = () => {
  if (typeof window !== 'undefined') {
    window.location.assign(signInUrlWithDestUrl(window.location.href));
  }
};

const isNotNullish = <T,>(x: T | null | undefined): x is T => {
  return x != null;
};

type CurrentAccountStoresProviderProps = {
  children: ReactNode;
};

export const CurrentAccountStoresProvider = ({children}: CurrentAccountStoresProviderProps) => {
  const currentAccountStoresQueryResult = useQuery<DeliveryManagementCurrentAccountStoresQuery>(
    CurrentAccountStoresQuery,
    {
      fetchPolicy: 'network-only',
      onCompleted: result => {
        if (!result.me?.catererAccount?.id) {
          performLoginRedirect();
        }
      },
      onError: () => {
        performLoginRedirect();
      },
      ssr: false,
    }
  );

  const catererAccount = currentAccountStoresQueryResult.data?.me?.catererAccount;

  if (currentAccountStoresQueryResult.loading || !catererAccount?.id) {
    return (
      <Layout>
        <MainContent>
          <Loader inline={false} />
        </MainContent>
      </Layout>
    );
  }

  const stores = (catererAccount?.stores?.nodes || []).filter(isNotNullish);

  return (
    <CurrentAccountStoresContext.Provider value={{stores}}>
      <Layout>
        <MainContent>{children}</MainContent>
      </Layout>
    </CurrentAccountStoresContext.Provider>
  );
};

export const useCurrentAccountStores = () => {
  return useContext(CurrentAccountStoresContext);
};
